/**
 * Calculate the factorial of a number
 *
 * @param {number} n - The number to calculate the factorial of
 * @returns {number} - The factorial of n
 *
 * @description n! = n * (n - 1) * (n - 2) * ... * 1
 * @see {https://www.dcode.fr/factorielle}
 */
export const factorial = (n: number): number => {
    if (n === 0 || n === 1) return 1
    let result = 1
    for (let i = 2; i <= n; i++) {
        result *= i
    }
    return result
}

/**
 * Calculate the number of combinations of k among n
 *
 * @param {number} n - Number of total elements
 * @param {number} k - Number of elements to choose
 * @returns {number} - Number of combinations of k among n
 *
 * @description n! / (k! * (n - k)!) is
 * @see {https://www.dcode.fr/combinaisons}
 */
export const combinations = (n: number, k: number): number => {
    if (k > n) return 0
    return factorial(n) / (factorial(k) * factorial(n - k))
}

/**
 * Calculate the number of permutations of k among n
 *
 * @param {number} n - Number of total elements
 * @param {number} k - Number of elements to choose
 * @returns {number} - Number of permutations of k among n
 *
 * @description n! / (n - k)!
 * @see {https://www.dcode.fr/permutations-generator}
 */
export const permutations = (n: number, k: number): number => {
    return factorial(n) / factorial(n - k)
}

/**
 * Calculate the number of permutations of k among n with a combination of c elements
 *
 * @param {number} n - Number of total elements
 * @param {number} k - Number of elements to choose
 * @param {number} c - Size of the combination
 * @returns {number} - Number of permutations of c among n
 */
export const permutationsPerCombinations = (
    n: number,
    k: number,
    c: number
): number => {
    // Number of combinations of c among n
    const comb = combinations(n, k)
    // Each combination of c values can be permuted in c! ways
    const permPerComb = factorial(c)
    return comb * permPerComb
}
